import { h, Fragment } from 'preact'
import PropTypes from 'prop-types'
import { useDispatch, useSelector } from 'react-redux'
import { useEffect } from 'preact/compat'
import classNames from 'classnames'
import QRCode from 'react-qr-code'
import { setAddCard, cardsSelector } from '../../../redux/slice/cardsSlice'
import CardForm from '../../CardForm'
import LinkButton from '../../Common/LinkButton'
import BillingAddress from '../../BillingAddress'
import PaymentInfo from '../../PaymentInfo'
import Group from '../../Common/Group'
import CardsVerificationInfo from '../../CardsVerificationInfo'
import SavedCards from '../../SavedCards'
import DirectDebit from '../../../assets/icons/direct-debit.svg'
import { useTranslations } from '../../../hooks/useTranslations'
import { PAYMENT_METHOD_TYPES } from '../../../constants/paymentMethods'
import PaymentPlan from '../../PaymentPlan'
import DirectDebitGuarantee from '../../DirectDebitGuarantee'
import {
  componentsLoadUpdate,
  isDetailsLoadedSelector,
  isPaymentMethodsLoadedSelector,
  pageStateSelector,
} from '../../../redux/slice/pageStateSlice'
import { paymentResponseSelector } from '../../../redux/slice/paymentResponseSlice'
import { transactionDetailsSelector } from '../../../redux/slice/transactionDetailsSlice'
import { selectIsPciFormEnabled } from '../../../redux/selectors/evervault'
import Email from '../../CardForm/Fields/Email/index'
import { isMobile } from '../../../helpers/common'
import { CARD_FIELDS } from '../../../constants/card'
import styles from '../../CardForm/Fields/CVV/CVV.module.scss'
import Input from '../../Common/Input/index.jsx'

const PaymentDetails = ({
  register,
  watch,
  errors,
  control,
  trigger,
  setValue,
  setFocus,
  clearErrors,
  paymentMethod,
  getValues,
  swishQrCode,
  theme,
}) => {
  const dispatch = useDispatch()
  const { savedCards, isAddCard } = useSelector(cardsSelector)
  const transactionDetails = useSelector(transactionDetailsSelector)
  const isPciFormEnabled = useSelector(selectIsPciFormEnabled)
  const paymentResponse = useSelector(paymentResponseSelector)
  const pageState = useSelector(pageStateSelector)
  const isPaymentMethodsLoaded = useSelector(isPaymentMethodsLoadedSelector)
  const isDetailsLoaded = useSelector(isDetailsLoadedSelector)
  const paymentInfoClasses = classNames([
    {
      hidden: transactionDetails?.hideAcceptedCards,
    },
    'payment-form__info',
  ])

  const setIsAddCard = state => {
    dispatch(setAddCard(state))

    window.parent?.postMessage?.(
      {
        type: 'MFEStoreDispatch',
        data: {
          actionType: 'setAddCard',
          message: state,
        },
      },
      '*',
    )
  }
  useEffect(() => {
    setValue(
      CARD_FIELDS.directDebitEmail,
      transactionDetails?.directDebit?.accountHolderEmail,
    )
    setValue(
      CARD_FIELDS.walleyEmail,
      transactionDetails?.directDebit?.accountHolderEmail,
    )
  }, [isPaymentMethodsLoaded])

  const l10n = useTranslations()

  const hasNewCardErrorAfterSubmit =
    isAddCard && paymentResponse.hasError && paymentResponse.hasResponse

  useEffect(() => {
    const isDetailsReady = !(
      (transactionDetails.isUserAuthenticated &&
        !pageState.areCardsLoaded &&
        paymentMethod === PAYMENT_METHOD_TYPES.Card) ||
      // check if tui-payments-methods loaded
      !isPaymentMethodsLoaded
    )

    dispatch(
      componentsLoadUpdate({
        isDetailsReady,
      }),
    )

    if (isPciFormEnabled && isDetailsReady && !isDetailsLoaded) {
      window.parent?.postMessage?.(
        {
          type: 'MFEStoreDispatch',
          data: {
            actionType: 'componentsLoadUpdate',
            message: { isDetailsReady },
          },
        },
        '*',
      )
    }
  }, [
    transactionDetails.isUserAuthenticated,
    pageState.areCardsLoaded,
    paymentMethod,
    pageState.displayedPaymentMethods,
    pageState.availableComponents,
    isPaymentMethodsLoaded,
  ])

  return (
    <tui-payments-details className="mfe-component">
      <form className="payment-form" data-testid="payment-details-form">
        {paymentMethod === PAYMENT_METHOD_TYPES.Card && (
          <Group type="card">
            <div className="card-form">
              <div className={paymentInfoClasses}>
                <PaymentInfo
                  prohibitedCardBrands={transactionDetails.prohibitedCardBrands}
                  prohibitedCardType={transactionDetails.prohibitedCardType}
                />
              </div>
              <div className="payment-form__form">
                {savedCards.length &&
                !isAddCard &&
                !hasNewCardErrorAfterSubmit ? (
                  <SavedCards
                    formProps={{ register, errors, setValue, trigger }}
                    handleAddCard={() => setIsAddCard(true)}
                  />
                ) : (
                  <>
                    <BillingAddress
                      register={register}
                      control={control}
                      watch={watch}
                      errors={errors}
                      setValue={setValue}
                      clearErrors={clearErrors}
                      defaultBillingAddress={transactionDetails.billingAddress}
                    />
                    <CardForm
                      register={register}
                      setValue={setValue}
                      setFocus={setFocus}
                      watch={watch}
                      errors={errors}
                      control={control}
                      trigger={trigger}
                      isMotoTransaction={!transactionDetails.is3dsRequired}
                      canSaveCard={transactionDetails.isUserAuthenticated}
                      shouldSaveCard={savedCards.length > 0}
                      clearErrors={clearErrors}
                      billingAddress={transactionDetails.billingAddress}
                      hideCardHolderName={transactionDetails.hideCardHolderName}
                      getValues={getValues}
                      theme={theme}
                    />
                    {savedCards.length > 0 && (
                      <div>
                        <LinkButton
                          data-testid="use-different-card-button"
                          id="use-different-card-add"
                          title={l10n.t('Use different card')}
                          onClick={() => setIsAddCard(false)}
                        />
                      </div>
                    )}
                  </>
                )}
                <CardsVerificationInfo
                  prohibitedCardBrands={transactionDetails.prohibitedCardBrands}
                />
              </div>
            </div>
          </Group>
        )}

        {paymentMethod === PAYMENT_METHOD_TYPES.DirectDebit && (
          <Group
            title={l10n.t('Direct Debit setup')}
            icon={DirectDebit}
            type="DD"
          >
            <PaymentPlan {...{ register, watch, errors, trigger }} />
            <DirectDebitGuarantee />
          </Group>
        )}
        {paymentMethod === PAYMENT_METHOD_TYPES.Walley && (
          <Group type="walley">
            <span className={styles.label}>
              {l10n.t('You will be sent an invoice to pay within 14 days')}
            </span>
            <Email
              register={register}
              errors={errors}
              label={l10n.t('Email')}
              type={CARD_FIELDS.walleyEmail}
            />
            <Input
              label={
                <>
                  {l10n.t('Mobile number')}
                  <span className={styles.label}>
                    {l10n.t(
                      'Only mobile phone numbers in Norway are able to use Walley',
                    )}
                  </span>
                </>
              }
              inputProps={{
                'data-apexx': 'mobile-number',
                'data-testid': 'mobile-number',
                name: 'mobileNumber',
                type: 'number',
                placeholder: '+47',
                id: CARD_FIELDS.walleyMobileNumber,
                ...register(CARD_FIELDS.walleyMobileNumber, {
                  required: true,
                  pattern: /^[+]?47\d{8}$/,
                }),
              }}
              hasError={
                errors && Boolean(errors[CARD_FIELDS.walleyMobileNumber])
              }
              errorMessage={l10n.t(
                'Please enter valid Norwegian mobile number',
              )}
            />
          </Group>
        )}
        {paymentMethod === PAYMENT_METHOD_TYPES.Swish &&
          !isMobile() &&
          swishQrCode && (
            <Group type="swish">
              <span className={styles.label}>
                {l10n.t('Open the Swish app and scan')}
              </span>
              <div>
                <QRCode
                  size={128}
                  style={{ marginTop: 10 }}
                  value={swishQrCode}
                  viewBox={`0 0 256 256`}
                />
              </div>
            </Group>
          )}
      </form>
    </tui-payments-details>
  )
}

PaymentDetails.propTypes = {
  register: PropTypes.func.isRequired,
  watch: PropTypes.func.isRequired,
  errors: PropTypes.object.isRequired,
  control: PropTypes.any.isRequired,
  trigger: PropTypes.func.isRequired,
  setValue: PropTypes.func.isRequired,
  setFocus: PropTypes.func.isRequired,
  clearErrors: PropTypes.func.isRequired,
  paymentMethod: PropTypes.string.isRequired,
  theme: PropTypes.string,
}

export default PaymentDetails
